import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  useTheme,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Divider,
  styled,
  Box,
  Button,
  Grid,
  alpha,
  Link,
} from "@mui/material";
import {
  ArrowForward,
  LocationOn,
  Work,
  ConnectWithoutContact,
  Star,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import ExhibitorModal from "./ExhibitorModal";
import { Language as WebsiteIcon } from "@mui/icons-material";
import { Edit, Delete } from "@mui/icons-material";

const StyledCard = styled(Card)(({ theme }) => ({
  position: "relative",
  overflow: "visible",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  borderRadius: theme.shape.borderRadius,
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: theme.shadows[6],
    "& .hover-content": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

const ImageWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  backgroundColor: "white",
  borderRadius: theme.shape.borderRadius,
  "&:hover img": {
    transform: "scale(1.05)",
  },
}));

const ExhibitorCard = ({
  refresh,
  exhibitor,
  exhibitors,
  role,
  onDelete,
  onEdit,
  user,
}) => {
  const theme = useTheme();
  const [openModal, setOpenModal] = useState(false);
  const { t } = useTranslation();
  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit(exhibitor);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm(t("confirmDeleteExhibitor"))) {
      onDelete(exhibitor.id);
    }
  };
  // console.log(exhibitor);
  return (
    <>
      <StyledCard>
        <ImageWrapper>
          <Box
            component="img"
            src={exhibitor.profile_pic || "./profile_pic.png"}
            alt={exhibitor.company_name}
            onClick={() => setOpenModal(true)}
            sx={{
              width: "100%",
              height: 240,
              objectFit: "contain",
              padding: 2,
              transition: "transform 0.3s ease",
              background: exhibitor.profile_pic
                ? "transparent"
                : `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.1
                  )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
            }}
          />

          <Box
            className="hover-content"
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              bgcolor: alpha(theme.palette.background.default, 0.9),
              opacity: 0,
              transform: "translateY(20px)",
              transition: "all 0.3s ease",
            }}
          >
            <Button
              fullWidth
              variant="contained"
              endIcon={<ArrowForward />}
              onClick={() => setOpenModal(true)}
              sx={{
                py: 1,
                borderRadius: 50,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {t("View Details")}
            </Button>
          </Box>
        </ImageWrapper>

        <CardContent sx={{ position: "relative", pt: 3 }}>
          {/* <Box sx={{ position: "absolute", top: -24, left: 16 }}>
            <Chip
              label={exhibitor.industryType}
              color="secondary"
              size="small"
              sx={{
                fontWeight: 700,
                boxShadow: theme.shadows[2],
              }}
            />
          </Box> */}

          <Typography
            variant="h6"
            sx={{
              mb: 1,
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontWeight: 800,
            }}
          >
            {exhibitor.company_name}
          </Typography>

          <Grid container spacing={1} sx={{ mb: 2 }}>
            {exhibitor.stallNo && (
              <Grid item>
                <Chip
                  icon={<LocationOn fontSize="small" />}
                  label={`${t("Stall")} #${exhibitor.stallNo}`}
                  variant="outlined"
                  size="small"
                  sx={{ borderRadius: 1 }}
                />
              </Grid>
            )}
            {/* {exhibitor.products && (
              <Grid item>
                <Chip
                  icon={<Work fontSize="small" />}
                  label={exhibitor.products.split(",").slice(0, 2).join(", ")}
                  variant="outlined"
                  size="small"
                  sx={{ borderRadius: 1 }}
                />
              </Grid>
            )} */}
          </Grid>

          <Divider
            sx={{
              my: 2,
              borderColor: alpha(theme.palette.divider, 0.1),
              borderBottomWidth: 2,
            }}
          />

          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LocationOn
                    fontSize="small"
                    sx={{
                      color: "text.secondary",
                      mr: 1,
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {exhibitor.country}
                  </Typography>
                </Box>

                <Box
                  component="a"
                  href={exhibitor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 1,
                    color: "text.secondary",
                    textDecoration: "none",
                    "&:hover": {
                      color: "primary.main",
                      textDecoration: "underline",
                    },
                  }}
                >
                  <WebsiteIcon fontSize="small" />
                  <Typography variant="body2">Website</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>

        {/* <IconButton
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: theme.shadows[3],
            "&:hover": {
              bgcolor: theme.palette.background.paper,
            },
          }}
          onClick={() => console.log("Connect clicked")}
        > 
          <ConnectWithoutContact />
        </IconButton>*/}
        {user?.email === "mohd.yahiya7@gmail.com" && (
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              display: "flex",
              gap: 1,
              zIndex: 2,
            }}
          >
            <IconButton
              onClick={handleEdit}
              sx={{
                bgcolor: alpha(theme.palette.background.paper, 0.9),
                boxShadow: theme.shadows[3],
                "&:hover": {
                  bgcolor: theme.palette.background.paper,
                },
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton
              onClick={handleDelete}
              sx={{
                bgcolor: alpha(theme.palette.error.main, 0.9),
                boxShadow: theme.shadows[3],
                "&:hover": {
                  bgcolor: theme.palette.error.main,
                },
              }}
            >
              <Delete fontSize="small" sx={{ color: "white" }} />
            </IconButton>
          </Box>
        )}
      </StyledCard>

      <ExhibitorModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        exhibitor={exhibitor}
        exhibitors={exhibitors}
        refresh={refresh}
        role={role}
      />
    </>
  );
};

// Keep existing propTypes and export

ExhibitorCard.propTypes = {
  exhibitor: PropTypes.shape({
    id: PropTypes.string.isRequired,
    company_name: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
    stallNo: PropTypes.string,
    fascia_board: PropTypes.string,
    profile_pic: PropTypes.string,
    company_profile: PropTypes.string,
    brands: PropTypes.string,
    isConnected: PropTypes.string,
  }).isRequired,
  exhibitors: PropTypes.array.isRequired,
  refresh: PropTypes.func.isRequired,
  role: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default ExhibitorCard;
